import React, { useEffect } from "react";

import { useIndependenceDayTheme } from "../../lib/useIndependenceDayTheme";
import { AshokaChakra } from "./IndiaFlag";

/* ------------------------------------------------------------------------
 * Site-wide Independence Day ambience.
 *
 * Design intent — restraint. This is a B2B fleet-tracking product, not a
 * festival microsite, so the theme reads as a considered brand moment rather
 * than decoration: a slow tricolour aurora behind the page, faint telemetry
 * routes that echo what the product actually does, and a Chakra watermark
 * turning almost imperceptibly.
 *
 * The whole layer sits at z-index -1 behind the page. Only the outermost page
 * canvases are made translucent (see the CSS) — every card, panel and section
 * keeps its solid surface, so the effect is depth and hierarchy rather than a
 * wash over the content. Text contrast is untouched.
 *
 * Everything animates transform / opacity / stroke-dashoffset only, so it
 * stays on the compositor. Nothing here is interactive and nothing shifts
 * layout: the layer is fixed and pointer-events: none.
 * ---------------------------------------------------------------------- */

const MOTES = [
    { left: 8, top: 22, size: 4, dur: 34, delay: 0, drift: 26 },
    { left: 19, top: 68, size: 3, dur: 44, delay: -8, drift: -18 },
    { left: 31, top: 14, size: 5, dur: 38, delay: -19, drift: 22 },
    { left: 44, top: 78, size: 3, dur: 52, delay: -5, drift: -28 },
    { left: 57, top: 33, size: 4, dur: 41, delay: -26, drift: 18 },
    { left: 68, top: 61, size: 3, dur: 47, delay: -12, drift: -22 },
    { left: 79, top: 19, size: 5, dur: 36, delay: -31, drift: 24 },
    { left: 91, top: 72, size: 4, dur: 50, delay: -17, drift: -20 },
];

export default function SiteAmbience() {
    const festive = useIndependenceDayTheme();

    /* One class on <html> is what switches every themed rule on. Removing it
     * (here, or by hand in devtools) reverts the entire site instantly. */
    useEffect(() => {
        const root = document.documentElement;
        if (festive) root.classList.add("id-fest");
        else root.classList.remove("id-fest");

        return () => root.classList.remove("id-fest");
    }, [festive]);

    if (!festive) return null;

    return (
        <div className="id-amb" aria-hidden="true">
            {/* 1. Base tricolour wash — the ground everything else sits on */}
            <div className="id-amb-base" />

            {/* 2. Aurora. Radial gradients rather than blur filters: the same
                   softness, a fraction of the paint cost when they move. */}
            <div className="id-amb-aurora id-amb-aurora-a" />
            <div className="id-amb-aurora id-amb-aurora-b" />
            <div className="id-amb-aurora id-amb-aurora-c" />

            {/* 3. Telemetry routes — on-brand, and what keeps this from
                   reading as generic festival dressing */}
            <div className="id-amb-routes">
                <svg viewBox="0 0 1440 900" preserveAspectRatio="none">
                    <path
                        className="id-amb-route"
                        d="M-60 250 C 260 190, 430 300, 720 232 S 1180 120, 1500 196"
                        fill="none"
                        stroke="#138808"
                        strokeOpacity=".30"
                        strokeWidth="1.6"
                        strokeDasharray="12 16"
                        style={{ animationDuration: "38s" }}
                    />
                    <path
                        className="id-amb-route"
                        d="M-60 520 C 300 470, 470 590, 780 508 S 1220 386, 1500 470"
                        fill="none"
                        stroke="#ff7722"
                        strokeOpacity=".28"
                        strokeWidth="1.6"
                        strokeDasharray="8 18"
                        style={{ animationDuration: "52s" }}
                    />
                    <path
                        d="M-60 700 C 340 660, 520 760, 830 690 S 1260 574, 1500 646"
                        fill="none"
                        stroke="#1a3a8f"
                        strokeOpacity=".14"
                        strokeWidth="1"
                    />

                    {/* Waypoints along the routes */}
                    <g className="id-amb-nodes">
                        <circle cx="430" cy="264" r="3" fill="#138808" fillOpacity=".5" />
                        <circle cx="1010" cy="168" r="3" fill="#138808" fillOpacity=".42" />
                        <circle cx="470" cy="551" r="3" fill="#ff7722" fillOpacity=".5" />
                        <circle cx="1080" cy="428" r="3" fill="#ff7722" fillOpacity=".42" />
                    </g>
                </svg>
            </div>

            {/* 4. Chakra watermark, one turn every four minutes */}
            <div className="id-amb-chakra">
                <AshokaChakra stroke="#1a3a8f" />
            </div>

            {/* 5. Drifting motes for depth */}
            {MOTES.map((m, i) => (
                <span
                    key={i}
                    className={`id-amb-mote id-amb-mote-${i % 3}`}
                    style={{
                        left: `${m.left}%`,
                        top: `${m.top}%`,
                        width: `${m.size}px`,
                        height: `${m.size}px`,
                        animationDuration: `${m.dur}s`,
                        animationDelay: `${m.delay}s`,
                        "--id-mote-drift": `${m.drift}px`,
                    }}
                />
            ))}

            {/* 6. Edge framing */}
            <div className="id-amb-vignette" />
        </div>
    );
}
